/**
 * Date and timezone utility functions for displaying timestamps
 * Converts UTC timestamps from backend to user's local timezone
 */

/**
 * Format a timestamp (milliseconds since epoch or ISO string) to a user-friendly string
 * in the user's local timezone
 * 
 * @param timestamp - Unix timestamp in milliseconds (UTC) or ISO date string
 * @param options - Intl.DateTimeFormatOptions for customization
 * @returns Formatted date string in user's local timezone
 */
export function formatTimestamp(
  timestamp: number | string | null | undefined,
  options?: Intl.DateTimeFormatOptions
): string {
  if (!timestamp) {
    return 'N/A';
  }

  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...options,
  };

  try {
    const date = new Date(timestamp);
    return new Intl.DateTimeFormat('en-US', defaultOptions).format(date);
  } catch (error) {
    // console.error('Error formatting timestamp:', error);
    return 'Invalid date';
  }
}

/**
 * Format a timestamp to a short date format (no time)
 * 
 * @param timestamp - Unix timestamp in milliseconds (UTC)
 * @returns Formatted date string (e.g., "Jan 15, 2024")
 */
export function formatDate(timestamp: number | null | undefined): string {
  return formatTimestamp(timestamp, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Format a timestamp to show relative time (e.g., "2 hours ago")
 * 
 * @param timestamp - Unix timestamp in milliseconds (UTC)
 * @returns Relative time string
 */
export function formatRelativeTime(timestamp: number | null | undefined): string {
  if (!timestamp) {
    return 'N/A';
  }

  try {
    const now = Date.now();
    const diff = now - timestamp;
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const weeks = Math.floor(days / 7);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);

    if (seconds < 60) {
      return 'Just now';
    } else if (minutes < 60) {
      return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    } else if (hours < 24) {
      return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    } else if (days < 7) {
      return `${days} day${days !== 1 ? 's' : ''} ago`;
    } else if (weeks < 4) {
      return `${weeks} week${weeks !== 1 ? 's' : ''} ago`;
    } else if (months < 12) {
      return `${months} month${months !== 1 ? 's' : ''} ago`;
    } else {
      return `${years} year${years !== 1 ? 's' : ''} ago`;
    }
  } catch (error) {
    // console.error('Error formatting relative time:', error);
    return 'Unknown';
  }
}

/**
 * Get the user's timezone
 * 
 * @returns IANA timezone string (e.g., "America/New_York")
 */
export function getUserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Get the user's timezone offset in minutes
 * 
 * @returns Timezone offset in minutes (e.g., -300 for EST)
 */
export function getTimezoneOffset(): number {
  return new Date().getTimezoneOffset();
}

/**
 * Format timestamp with timezone indicator
 * 
 * @param timestamp - Unix timestamp in milliseconds (UTC)
 * @returns Formatted date string with timezone (e.g., "Jan 15, 2024, 3:30 PM EST")
 */
export function formatTimestampWithTimezone(timestamp: number | null | undefined): string {
  if (!timestamp) {
    return 'N/A';
  }

  try {
    const date = new Date(timestamp);
    const formatted = new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
    }).format(date);
    
    return formatted;
  } catch (error) {
    // console.error('Error formatting timestamp with timezone:', error);
    return 'Invalid date';
  }
}
